import { PROJECTS } from "./projects";
import technologySlugs from "./technology-slugs.json";

/**
 * Keep TECHNOLOGY_SLUGS in sync with technology-slugs.json (used by gatsby-node).
 */
export const TECHNOLOGIES = [
  {
    slug: "react",
    title: "React based development",
    navLabel: "React",
    icon: "React",
    seoTitle: "React developer for SaaS",
    seoDescription:
      "Senior React engineer for established SaaS products - design systems, performance, and frontend architecture.",
    description:
      "I use React on established SaaS products - design systems, complex UI, and client apps that need to stay maintainable as the product grows.",
    features: [
      "Reusable components",
      "Strong performance habits",
      "Scalable app structure",
      "Works well with TypeScript",
      "Design system friendly",
    ],
    projectSlugs: [
      "condexo-pay",
      "condexo-property-management",
      "shakespeers",
      "harmindra-international",
    ],
    advantages: {
      title: "Why React works well here",
      items: [
        "Components make it easier to reuse UI across a product",
        "A big ecosystem of libraries when you need them",
        "Good fit for design systems and long-lived SaaS apps",
        "Strong tooling and hiring pool for product teams",
      ],
    },
    note: {
      title: "How I have used React",
      body: "Most of my React work has been on products that already had users. Payment flows at Condexo, a booking platform at Shakespeers, and a manufacturer site built with Gatsby. The common thread is keeping the UI clear while the product keeps changing.",
    },
  },
  {
    slug: "vue",
    title: "Vue based development",
    navLabel: "Vue",
    icon: "Vue",
    seoTitle: "Vue developer for SaaS",
    seoDescription:
      "Senior Vue engineer for enterprise SaaS - component libraries, web components, and long-lived product frontends.",
    description:
      "I use Vue on established product frontends, including design systems and web components, where clear structure and steady delivery matter.",
    features: [
      "Component-driven UI",
      "Design system work",
      "Web components fit",
      "Clear data flow",
      "Easy for teams to extend",
    ],
    projectSlugs: ["meltwater", "jiffy"],
    advantages: {
      title: "Why Vue works well here",
      items: [
        "Clear structure that teams can pick up quickly",
        "Works nicely with design systems and shared components",
        "Fits well next to web components in larger products",
        "Steady and readable for day-to-day product work",
      ],
    },
    note: {
      title: "How I have used Vue",
      body: "At Meltwater I work on a publishing product built with Vue and Stencil web components. Before that, Jiffy was a large Vue codebase tied into Rails. I spend a lot of time on shared UI, calendars, and making sure new screens fit the system the team already has.",
    },
  },
];

export const TECHNOLOGY_SLUGS = TECHNOLOGIES.map((tech) => tech.slug);

const slugsMatchRegistry =
  TECHNOLOGY_SLUGS.length === technologySlugs.length &&
  TECHNOLOGY_SLUGS.every((slug, index) => slug === technologySlugs[index]);

if (!slugsMatchRegistry) {
  throw new Error(
    "technology-slugs.json is out of sync with TECHNOLOGIES. Update both so gatsby-node createPages stays correct."
  );
}

export const TECHNOLOGY_BADGE_ALIASES = {
  React: "react",
  Vue: "vue",
  "Gatsby (React)": "react",
};

export const getTechnologyBySlug = (slug) =>
  TECHNOLOGIES.find((tech) => tech.slug === slug) || null;

export const getTechnologyPath = (slug) => `/technology/${slug}/`;

export const getTechnologyHrefForBadge = (badgeLabel) => {
  const slug = TECHNOLOGY_BADGE_ALIASES[badgeLabel];
  return slug ? getTechnologyPath(slug) : null;
};

export const getProjectsForTechnology = (technology) => {
  if (!technology?.projectSlugs?.length) {
    return [];
  }

  const bySlug = new Map(PROJECTS.map((project) => [project.slug, project]));
  return technology.projectSlugs
    .map((slug) => bySlug.get(slug))
    .filter(Boolean);
};
